import React, { useState } from 'react';
import { MoreHorizontal, Eye, Trash2, Plus, Edit, ChevronLeft, ChevronRight } from 'lucide-react';
import AddProjectModal from '../components/AddProjectModal';
import ViewProjectModal from '../components/ViewProjectModal';
import EditCompanyProfileModal from '../components/EditCompanyProfileModal';
import DeleteConfirmationModal from '../../../shared/components/modals/DeleteConfirmationModal';
import TableSkeleton from '../components/Skeletons/TableSkeleton';
import SuccessToast from '../../../shared/components/ui/SuccessToast';
import './Projects.css';
import api from '../../../services/api';

const FILTERS = ['All', 'Completed', 'In Progress'];

const Projects = ({ user }) => {
    const userDesignation = (user?.designation || '').toLowerCase();
    const userRole = (user?.role || '').toLowerCase();
    const isManager = userDesignation.includes('lead') || userDesignation.includes('manager') || userDesignation.includes('admin') || userRole.includes('lead') || userRole.includes('manager') || userRole.includes('admin');

    const [activeFilter, setActiveFilter] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [projectToDelete, setProjectToDelete] = useState(null);
    const [projectToEdit, setProjectToEdit] = useState(null);
    const [projectToView, setProjectToView] = useState(null);
    const [showEditProfile, setShowEditProfile] = useState(false);
    const [projects, setProjects] = useState([]);
    const [companyProfile, setCompanyProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeMenuId, setActiveMenuId] = useState(null);
    const [successToast, setSuccessToast] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const menuRef = React.useRef(null);
    const ITEMS_PER_PAGE = 10;

    React.useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, activeFilter]);

    // Close menu on outside click
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (activeMenuId && menuRef.current && !menuRef.current.contains(event.target)) {
                setActiveMenuId(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [activeMenuId]);

    const fetchProjects = async () => {
        try {
            const response = await api.get('/experience');
            if (response.status === 200) {
                const data = response.data;
                const pList = Array.isArray(data) ? data : (data.projects || []);
                setProjects(pList);
            }
        } catch (err) {
            console.error('Failed to fetch projects', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchCompanyProfile = async () => {
        try {
            const response = await api.get('/company/profile');
            if (response.status === 200) {
                setCompanyProfile(response.data);
            }
        } catch (err) {
            if (err.response && err.response.status === 404) {
                // Ignore 404 errors, fallback text will be shown
            } else {
                console.error('Failed to fetch company profile', err);
            }
        }
    };

    React.useEffect(() => {
        fetchProjects();
        fetchCompanyProfile();
    }, []);

    const filtered = projects.filter((p) => {
        const pStatus = (p.status || '').toUpperCase().replace(/[^A-Z]/g, '');
        const fStatus = activeFilter.toUpperCase().replace(/[^A-Z]/g, '');

        const matchesFilter =
            activeFilter === 'All' ||
            (fStatus === 'INPROGRESS' && pStatus !== 'COMPLETED') ||
            pStatus === fStatus;
        const matchesSearch =
            (p.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (p.client || '').toLowerCase().includes(searchQuery.toLowerCase());
        return matchesFilter && matchesSearch;
    });

    const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
    const paginatedProjects = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    const allCount = projects.length;
    const completedCount = projects.filter((p) => {
        const s = (p.status || '').toUpperCase().replace(/[^A-Z]/g, '');
        return s === 'COMPLETED' || s === 'COMPLETE';
    }).length;
    const inProgressCount = allCount - completedCount;

    const handleAddProject = (projectData, msg) => {
        setShowModal(false);
        fetchProjects(); // Refresh the list from the server to get the real ID
        if (msg) {
            setSuccessToast({ message: msg, rfpName: projectData?.title });
            setTimeout(() => setSuccessToast(null), 3500);
        }
    };

    const requestEditProject = (project) => {
        setProjectToView(project);
        setProjectToEdit(project);
    };

    const requestViewProject = (project) => {
        setProjectToView(project);
    };

    const handleEditProject = (projectData, msg) => {
        setProjectToEdit(null);
        fetchProjects();
        if (msg) {
            setSuccessToast({ message: msg, rfpName: projectData?.title });
            setTimeout(() => setSuccessToast(null), 3500);
        }
    };

    const requestDeleteProject = (project) => {
        setProjectToDelete(project);
    };

    const confirmDeleteProject = async (project) => {
        const projectId = project.project_id || project.id || project._id;
        try {
            const response = await api.delete(`/experience/${projectId}`);
            if (response.status === 200 || response.status === 204) {
                setProjects((prev) => prev.filter((p) => (p.project_id || p.id || p._id) !== projectId));
                setProjectToDelete(null);
                setSuccessToast({ message: response.data?.message || 'Project deleted successfully', rfpName: project.title });
                setTimeout(() => setSuccessToast(null), 3500);
                return true;
            }
        } catch (err) {
            console.error('Failed to delete project', err);
        }
        return false;
    };

    return (
        <div className="projects-page">
            {/* About Us Section */}
            <div className="about-section">
                <h2 className="about-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>About <span className="about-title-accent">{companyProfile?.company_name || 'Exalogic'}</span></span>
                    {isManager && (
                        <button className="edit-profile-btn" onClick={() => setShowEditProfile(true)} title="Edit Company Profile">
                            <Edit size={16} />
                        </button>
                    )}
                </h2>
                <div className="about-content-wrapper">
                    {companyProfile?.description ? (
                        companyProfile.description.split('\n').filter(p => p.trim()).map((paragraph, index) => (
                            <p key={index} className="about-text">{paragraph}</p>
                        ))
                    ) : (
                        <>
                            <p className="about-text">
                                Exalogic is a leading technology solutions provider specializing in digital transformation, cloud computing, enterprise software development, and artificial intelligence solutions. Headquartered in the region, the company develops cutting-edge products across cyber technologies, automation, and advanced systems integration.
                            </p>
                            <p className="about-text">
                                With a strong focus on innovation and strategic collaboration, Exalogic delivers next-generation software platforms that enhance operational efficiency, security, and digital capabilities for both enterprise and government sectors.
                            </p>
                        </>
                    )}

                    {companyProfile?.capabilities && companyProfile.capabilities.length > 0 && (
                        <div className="about-capabilities">
                            {companyProfile.capabilities.map((cap, idx) => (
                                <div key={idx} className="capability-item">
                                    <span className="capability-bullet">•</span>
                                    <span className="capability-text">{cap}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Header */}
            <div className="projects-header">
                <div className="projects-header-left">
                    <h1 className="projects-title">Project <span style={{ color: '#FF5526' }}>Portfolio</span></h1>
                    <p className="projects-subtitle">Showcasing our strategic delivery capabilities, execution excellence, and proven track record.</p>
                </div>
                {isManager && (
                    <button className="upload-project-btn" onClick={() => setShowModal(true)}>
                        <Plus size={16} />
                        Add Project
                    </button>
                )}
            </div>

            {/* Filters + Search */}
            <div className="projects-toolbar">
                <div className="projects-search-wrap">
                    <span className="search-icon-inner">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
                    </span>
                    <input
                        className="projects-search"
                        type="text"
                        placeholder="Search projects..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <div className="projects-filters">
                    {FILTERS.map((f) => {
                        let count = allCount;
                        if (f === 'Completed') count = completedCount;
                        if (f === 'In Progress') count = inProgressCount;

                        return (
                            <button
                                key={f}
                                className={`filter-btn ${activeFilter === f ? 'filter-btn--active' : ''}`}
                                onClick={() => setActiveFilter(f)}
                            >
                                {f} ({count})
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Dashboard List Header */}
            <div className="projects-list-container">
                <div className="projects-list-header">
                    <div className="list-col col-title">Project Name</div>
                    <div className="list-col col-client">Client</div>
                    <div className="list-col col-status">Status</div>
                    <div className="list-col col-value">Valuation</div>
                    <div className="list-col col-actions">ACTION</div>
                </div>

                <div className="projects-list">
                    {loading ? (
                        <TableSkeleton />
                    ) : paginatedProjects.length === 0 ? (
                        <div className="projects-empty">No projects found.</div>
                    ) : (
                        paginatedProjects.map((project, idx) => (
                            <ProjectRow
                                key={project.project_id || project.id || project._id || idx}
                                project={project}
                                onDelete={requestDeleteProject}
                                onEdit={requestEditProject}
                                onView={requestViewProject}
                                activeMenuId={activeMenuId}
                                setActiveMenuId={setActiveMenuId}
                                menuRef={menuRef}
                                isManager={isManager}
                            />
                        ))
                    )}
                </div>
                {totalPages > 1 && (
                    <div className="pagination-controls" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderTop: '1px solid #E2E8F0' }}>
                        <div style={{ fontSize: '13px', color: '#64748B' }}>
                            Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} out of {filtered.length} projects
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button 
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '6px', border: '1px solid #E2E8F0', background: currentPage === 1 ? '#F8FAFC' : '#FFF', color: currentPage === 1 ? '#CBD5E1' : '#64748B', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}
                            >
                                <ChevronLeft size={16} />
                            </button>
                            
                            {[...Array(totalPages)].map((_, i) => {
                                const pageNum = i + 1;
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => setCurrentPage(pageNum)}
                                        style={{ 
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '6px', 
                                            border: pageNum === currentPage ? '1px solid #FF5526' : '1px solid #E2E8F0', 
                                            background: pageNum === currentPage ? '#FF5526' : '#FFF', 
                                            color: pageNum === currentPage ? '#FFF' : '#64748B', 
                                            fontWeight: pageNum === currentPage ? '600' : '400',
                                            cursor: 'pointer', transition: 'all 0.2s', fontSize: '13px' 
                                        }}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}

                            <button 
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '6px', border: '1px solid #E2E8F0', background: currentPage === totalPages ? '#F8FAFC' : '#FFF', color: currentPage === totalPages ? '#CBD5E1' : '#64748B', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modals */}
            {showModal && (
                <AddProjectModal
                    onClose={() => setShowModal(false)}
                    onAdd={handleAddProject}
                />
            )}

            {projectToView && (
                <ViewProjectModal
                    project={projectToView}
                    onClose={() => {
                        setProjectToView(null);
                        setProjectToEdit(null);
                    }}
                    isManager={isManager}
                    initialMode={projectToEdit ? 'edit' : 'view'}
                    onEditSuccess={(updatedProj, msg) => {
                        handleEditProject(updatedProj, msg);
                        setProjectToEdit(null);
                    }}
                    onDelete={(proj) => {
                        setProjectToView(null);
                        setProjectToEdit(null);
                        requestDeleteProject(proj);
                    }}
                />
            )}

            {showEditProfile && (
                <EditCompanyProfileModal
                    profile={companyProfile}
                    onClose={() => setShowEditProfile(false)}
                    onSave={(updatedProfile) => {
                        setCompanyProfile(updatedProfile);
                        setShowEditProfile(false);
                    }}
                />
            )}

            {projectToDelete && (
                <DeleteConfirmationModal
                    project={projectToDelete}
                    onClose={() => setProjectToDelete(null)}
                    onDeleteConfirm={confirmDeleteProject}
                />
            )}

            <SuccessToast toast={successToast} />
        </div>
    );
};

const ProjectRow = ({ project, onDelete, onEdit, onView, activeMenuId, setActiveMenuId, menuRef, isManager }) => {
    const sNorm = (project.status || '').toUpperCase().replace(/[^A-Z]/g, '');
    const isCompleted = sNorm === 'COMPLETED' || sNorm === 'COMPLETE';
    const projectId = project.project_id || project.id || project._id;

    const handleDelete = (e) => {
        e.stopPropagation();
        onDelete(project);
    };

    const toTitleCase = (str) => {
        if (!str) return '';
        return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    return (
        <div className="project-row" onClick={() => onView(project)}>
            <div className="list-col col-title">
                <span className="project-name-text">{toTitleCase(project.title)}</span>
            </div>
            <div className="list-col col-client">
                <span className="project-client-text">{toTitleCase(project.client)}</span>
            </div>
            <div className="list-col col-status">
                <span className={`project-status-badge ${isCompleted ? 'status-completed' : 'status-inprogress'}`}>
                    {isCompleted ? 'Completed' : 'In Progress'}
                </span>
            </div>
            <div className="list-col col-value">
                <span className="project-value-text">{project.completed_date || project.value || 'N/A'}</span>
            </div>
            <div className="list-col col-actions">
                <div className="actions-inline-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                    {!isManager ? (
                        <button
                            className="action-icon-btn"
                            onClick={(e) => { e.stopPropagation(); onView(project); }}
                            title="View Details"
                        >
                            <Eye size={16} />
                        </button>
                    ) : (
                        <>
                            <button
                                className="action-icon-btn"
                                onClick={(e) => { e.stopPropagation(); onView(project); }}
                                title="View Details"
                            >
                                <Eye size={16} />
                            </button>
                            <button
                                className="action-icon-btn"
                                onClick={(e) => { e.stopPropagation(); onEdit(project); }}
                                title="Edit Project"
                            >
                                <Edit size={16} />
                            </button>
                            <button
                                className="action-icon-btn delete-btn"
                                onClick={handleDelete}
                                title="Delete Project"
                            >
                                <Trash2 size={16} />
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Projects;
